
import { useCallback, useEffect, useState } from 'react';
import { Node } from '@xyflow/react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AutomationNodeData, NodeType } from '@/types/automation';
import { useAgents } from '@/hooks/useAgents';
import { Agent } from '@/types/agent';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface NodeConfigPanelProps {
  selectedNode: Node<AutomationNodeData> | null;
  onNodeUpdate: (nodeId: string, data: AutomationNodeData) => void;
  onNodeDelete: (nodeId: string) => void;
}

export const NodeConfigPanel = ({ selectedNode, onNodeUpdate, onNodeDelete }: NodeConfigPanelProps) => {
  const { agents } = useAgents();
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (selectedNode?.data?.agentId && agents) {
      const agent = agents.find(a => a.id === Number(selectedNode.data.agentId));
      if (agent) {
        setSelectedAgent(agent);
      }
    }
  }, [selectedNode?.data?.agentId, agents]);

  const updateNodeData = useCallback((key: string, value: string | number | string[]) => {
    if (!selectedNode) return;
    
    const updatedData = {
      ...selectedNode.data,
      [key]: value,
    };
    
    onNodeUpdate(selectedNode.id, updatedData);
  }, [selectedNode, onNodeUpdate]);

  const handleAgentChange = (agentId: string) => {
    if (!agents) return;
    
    const agent = agents.find(a => a.id === Number(agentId));
    setSelectedAgent(agent || null);
    updateNodeData('agentId', Number(agentId));
    updateNodeData('agentName', agent?.name || '');
  };

  const handleTriggerChange = (triggerType: string) => {
    updateNodeData('triggerType', triggerType);
  };

  const savePrompt = async () => {
    if (!selectedNode || !selectedAgent) return;
    
    try {
      const promptToSave = selectedNode.data.prompt || '';
      
      const { data, error } = await supabase
        .from('agent_custom_prompts')
        .select('*')
        .eq('agent_id', selectedAgent.id)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      
      if (data) {
        const { error: updateError } = await supabase
          .from('agent_custom_prompts')
          .update({ 
            prompt: promptToSave,
            updated_at: new Date()
          })
          .eq('id', data.id);
          
        if (updateError) throw updateError;
        
        toast({
          title: "Prompt updated",
          description: "The agent's prompt was updated successfully."
        });
      } else {
        const { error: insertError } = await supabase
          .from('agent_custom_prompts')
          .insert([{ 
            agent_id: selectedAgent.id,
            prompt: promptToSave,
            created_at: new Date(),
            updated_at: new Date()
          }]);
          
        if (insertError) throw insertError;
        
        toast({
          title: "Prompt created",
          description: "The agent's prompt was created successfully."
        });
      }
    } catch (error) {
      console.error("Error saving prompt:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save the prompt."
      });
    }
  };

  if (!selectedNode) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle>Configuration of Node</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Select a node to configure.</p>
        </CardContent>
      </Card>
    );
  }

  const { type, data } = selectedNode;

  return (
    <Card className="h-full overflow-auto">
      <CardHeader>
        <CardTitle>
          {type === 'trigger' && 'Configuration of Trigger'}
          {type === 'message' && 'Configuration of Message'}
          {type === 'condition' && 'Configuration of Condition'}
          {type === 'delay' && 'Configuration of Delay'}
          {type === 'aiAgent' && 'Configuration of AI Agent'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="nodeName">Name</Label>
          <Input 
            id="nodeName" 
            value={data.label || ''} 
            onChange={(e) => updateNodeData('label', e.target.value)}
            placeholder="Node name"
          />
        </div>

        {type === 'trigger' && (
          <div className="space-y-2">
            <Label htmlFor="triggerType">Trigger Type</Label>
            <Select 
              value={data.triggerType || 'message'} 
              onValueChange={handleTriggerChange}
            >
              <SelectTrigger id="triggerType">
                <SelectValue placeholder="Select trigger type" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="message">Message Received</SelectItem>
                  <SelectItem value="keyword">Keyword</SelectItem>
                  <SelectItem value="newContact">New Contact</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>

            {data.triggerType === 'keyword' && (
              <div className="mt-4 space-y-2">
                <Label htmlFor="keywords">Keywords (comma-separated)</Label>
                <Input 
                  id="keywords" 
                  value={data.keywords || ''} 
                  onChange={(e) => updateNodeData('keywords', e.target.value)}
                  placeholder="Ex: help, support, information"
                />
              </div>
            )}
          </div>
        )}

        {type === 'message' && (
          <div className="space-y-2">
            <Label htmlFor="messageText">Message Text</Label>
            <Textarea 
              id="messageText" 
              value={data.messageText || ''} 
              onChange={(e) => updateNodeData('messageText', e.target.value)}
              placeholder="Type your message here..."
              className="h-32"
            />
          </div>
        )}

        {type === 'condition' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="conditionType">Condition Type</Label>
              <Select 
                value={data.conditionType || 'contains'} 
                onValueChange={(value) => updateNodeData('conditionType', value)}
              >
                <SelectTrigger id="conditionType">
                  <SelectValue placeholder="Select condition type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="contains">Contains Text</SelectItem>
                    <SelectItem value="equals">Equal to</SelectItem>
                    <SelectItem value="startsWith">Starts with</SelectItem>
                    <SelectItem value="endsWith">Ends with</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="conditionValue">Condition Value</Label>
              <Input 
                id="conditionValue" 
                value={data.conditionValue || ''} 
                onChange={(e) => updateNodeData('conditionValue', e.target.value)}
                placeholder="Text to be verified"
              />
            </div>
          </div>
        )}

        {type === 'delay' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="delayAmount">Delay Amount</Label>
              <Input 
                id="delayAmount" 
                type="number" 
                value={data.delayAmount || 5} 
                onChange={(e) => updateNodeData('delayAmount', Number(e.target.value))}
                min="1"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="delayUnit">Unit</Label>
              <Select 
                value={data.delayUnit || 'minutes'} 
                onValueChange={(value) => updateNodeData('delayUnit', value)}
              >
                <SelectTrigger id="delayUnit">
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="seconds">Seconds</SelectItem>
                    <SelectItem value="minutes">Minutes</SelectItem>
                    <SelectItem value="hours">Hours</SelectItem>
                    <SelectItem value="days">Days</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {selectedNode?.type === 'aiAgent' && agents && agents.length > 0 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="agentId">Select AI Agent</Label>
              <Select 
                value={selectedNode.data.agentId?.toString() || ''} 
                onValueChange={handleAgentChange}
              >
                <SelectTrigger id="agentId">
                  <SelectValue placeholder="Select an agent" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {agents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id.toString()}>
                        {agent.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {selectedAgent && (
              <div className="space-y-2">
                <Label htmlFor="prompt">Customize Agent Prompt</Label>
                <Textarea 
                  id="prompt" 
                  value={selectedNode.data.prompt || ''} 
                  onChange={(e) => updateNodeData('prompt', e.target.value)}
                  placeholder="Add custom instructions for this agent..."
                  className="h-32"
                />
                <Button
                  onClick={savePrompt}
                  size="sm"
                  className="mt-2"
                >
                  Save Prompt
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="pt-4 flex justify-end">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onNodeDelete(selectedNode.id)}
          >
            Delete Node
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
